/*
 * @file push/push.ts
 * Relution SDK
 *
 * Created by Thomas Beckmann on 06.07.2016
 * Copyright 2016 M-Way Solutions GmbH
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * @module push
 */
/** */

import * as _ from 'lodash';
import * as Q from 'q';

import * as diag from '../core/diag';
import * as device from '../core/device';
import * as domain from '../core/domain';
import * as init from '../core/init';
import * as server from '../security/server';
import * as web from '../web';

import {Filter, NullFilter, StringFilter, StringEnumFilter} from '../query/Filter';
import {User} from '../security/roles';

/**
 * endpoint URL of push REST API set up by CLI project generation by default.
 *
 * @type {string}
 */
const pushUrl = 'api/v1/push';

/**
 * transmission status of a push Job.
 *
 * This models the Java enum `com.mwaysolutions.gofer2.push.domain.State`.
 */
export type State = 'UNUSED' | 'QUEUED' | 'RUNNING' | 'FINISHED';

/**
 * represents a push message.
 *
 * This models the Java class `com.mwaysolutions.gofer2.push.domain.Job`.
 */
export interface Job extends domain.Referenceable/*, domain.HasModified*/ {
  description?: string;
  state?: State;
  app?: App;

  message: string;
  badge?: string;
  sound?: string;
  extras?: _.Dictionary<string>;

  deviceFilter?: Filter;

  total?: number;
  sent?: number;
  failed?: number;

  // incompatible to domain.HasModified,
  // likely changed in the future without notice
  // createdAt?: Date;
  // modifiedAt?: Date;
}

/**
 * represents a push configuration of a specific client app.
 *
 * This models the Java class `com.mwaysolutions.gofer2.push.domain.App`.
 */
export interface App extends domain.Referenceable, domain.Secure, domain.HasVersion,
                             domain.HasBundle, domain.HasApplication/*, domain.HasModified*/ {
  organisationUuid?: string;
  name?: string;
  description?: string;

  // apnsProvider?: any; // not modelled explicitly, not exposed to clients
  // gcmProvider?: any;  // not modelled explicitly, not exposed to clients
  // wnsProvider?: any;  // not modelled explicitly, not exposed to clients

  devices?: Device[];    // (usually) not exposed to clients
  jobs?: Job[];          // (usually) not exposed to clients

  // incompatible to domain.HasModified,
  // likely changed in the future without notice
  // createdAt?: Date;
  // modifiedAt?: Date;
  // deletedAt?: Date;
}

/**
 * communication provider used to transport push messages to a push Device.
 *
 * This models the Java enum `com.mwaysolutions.gofer2.push.domain.ProviderType`.
 */
export type ProviderType = 'GCM' | 'APNS' | 'MCAP' | 'WNS' | 'PAP';

/**
 * represents a push information of a specific mobile device.
 *
 * This models the Java class `com.mwaysolutions.gofer2.push.domain.Device`.
 */
export interface Device extends domain.Referenceable/*, domain.HasModified*/ {
  token?: string;
  providerType?: ProviderType;
  app?: App;

  deviceIdentifier?: string;
  appPackageName?: string;

  user?: string;
  vendor?: string;
  name?: string;

  osVersion?: string;
  language?: string;
  country?: string;
  type?: string;
  appVersion?: string;
  model?: string;

  attributes?: _.Dictionary<string>;

  tags?: string[];
  badge?: number;

  // incompatible to domain.HasModified,
  // likely changed in the future without notice
  // createdAt?: Date;
  // modifiedAt?: Date;
  // deletedAt?: Date;
  lastConnect?: Date;
}

/**
 * set of application specific options for registerPushNotification().
 */
export interface RegistrationOptions {
  attributes?: _.Dictionary<string>;
  tags?: string[];
  badge?: number;
};

/**
 * registers a target device with the current Relution server.
 *
 * The implementation relies on backend code generated by CLI. That code attempts fetching an
 * existing device using the metadata information send as request body data. If it finds one, that
 * device is updated. Otherwise a new device is created and stored in the database. The updated
 * device registration record then is sent as response body data.
 *
 * @param registrationId to register.
 * @return promise of registered device.
 *
 * @internal SDK client code must call configurePushDevice() which obtains the token.
 */
export function registerPushDevice(registrationId: string,
                                   options: RegistrationOptions = {}): Q.Promise<Device> {
  const user = server.getCurrentAuthorization().name;
  const pushInitOptions = init.initOptions.push;
  return device.ready.then((info) => {
    let providerType: ProviderType;
    switch (info.platform.id) {
      case 'android':
        providerType = 'GCM';
        break;
      case 'ios':
        // when a senderID is configured,
        // iOS device is registered at APNS which generates a token registered in turn at GCM,
        // so that pushes are send using GCM to either type of device.
        providerType = pushInitOptions.ios.senderID ? 'GCM' : 'APNS';
        break;
      case 'windowsphone':
        providerType = 'WNS';
        break;
      case 'blackberry':
        providerType = 'PAP';
        break;
      default:
        providerType = 'MCAP';
        break;
    }
    diag.debug.assert(!!info.device, 'The current implementation supports mobile devices only!');
    return <Device>{
      uuid: null,
      providerType: providerType,
      token: registrationId,

      user: user,
      deviceIdentifier: info.uuid,
      vendor: info.device.manufacturer,
      name: info.device.name,
      osVersion: info.device.version,
      model: info.device.model,
      type: info.device.name,

      appPackageName: info.device.appIdentifier,
      appVersion: info.device.appVersionCode || info.device.appVersionName,

      attributes: options.attributes,
      tags: options.tags,
      badge: options.badge
    };
  }).then((device: Device) => {
    diag.debug.assert(device.providerType !== 'PAP', 'Relution Server does not yet support this!');
    diag.debug.assert(device.providerType !== 'MCAP', 'Relution SDK does not yet implement this!');
    return web.post<Device>(pushUrl + '/registration', device);
  });
}

/**
 * posts push notification(s).
 *
 * Usually the server sends push notifications on its own behalf. However, this method may be used
 * by the client app itself to send push notifications either to other clients, or to itself which
 * can be used to test connectivity.
 *
 * The implementation relies on backend code generated by CLI which forwards the body JSON to the
 * server-side implementation of this method.
 *
 * @param message to deliver.
 * @returns promise of async execution resolving to UUIDs of jobs in asynchronous delivery,
 *    empty when no apps or devices got selected by the message criteria or null when no
 * 		target apps or devices exist at all.
 */
export function postPushNotification(message: Job): Q.Promise<string[]> {
  return web.post<string[]>(pushUrl, message);
}

/**
 * creates a device filter for the user attribute of push devices matching any of a given set of
 * users.
 *
 * @param users* to filter on.
 * @returns device filter matching devices of given users.
 */
export function pushDeviceFilterByUsers(...users: (User | string)[]): Filter {
  if (users.length <= 0) {
    return <NullFilter>{
      type: 'null',
      fieldName: 'user',
      isNull: true
    };
  } else if (users.length === 1) {
    return <StringFilter>{
      type: 'string',
      fieldName: 'user',
      value: domain.uuidOf(users[0])
    };
  } else {
    const uuids = users.map((user) => domain.uuidOf(user));
    return <StringEnumFilter>{
      type: 'stringEnum',
      fieldName: 'user',
      values: uuids
    };
  }
}

/**
 * gets push notification status.
 *
 * The implementation relies on backend code generated by CLI which uses the server-side
 * PushService to query for Job by uuid.
 *
 * @param uuidOrMessage to query.
 * @returns promise of async execution resolving to push Job information.
 */
export function fetchPushNotification(uuidOrMessage: string | Job): Q.Promise<Job> {
  const uuid = _.isString(uuidOrMessage) ? uuidOrMessage : uuidOrMessage.uuid;
  return web.get<Job>(pushUrl + '/' + uuid);
}
